import { CompareLayer, CompareLayerProps } from '@oneaudi/audi-compare-layer-package';
import { ActiveTab } from '@oneaudi/audi-compare-layer-package/dist/types';
import { useAtom, useAtomValue } from 'jotai';
import { useEffect, useState } from 'react';
import {
	DriveTrainModelId,
	getCorrectDriveTrain,
} from '~/app/compositions/sidebar/trim-line/utils/get-correct-drive-train';
import { getCorrectTrimline } from '~/app/compositions/sidebar/trim-line/utils/get-correct-trimline';
import { useAppServices } from '~/app/contexts/app-context';
import { useMarketConfig } from '~/app/market-config';
import { createCarlineInput } from '~/app/utils/create-carline-input';
import { useDebugMode } from '~/app/utils/debug-mode';
import { useActiveCard } from '~/components/slider-panel';
import { useCatalog, useConfigurator } from '~/graphql/hooks/use-configurator';
import { useConfiguredCarDynamicCatalog } from '~/graphql/hooks/use-configured-car-dynamic-catalog';
import { useCarlineQuery } from '~/graphql/hooks/use-configured-car-queries';
import { useConfiguredCarStaticCatalog } from '~/graphql/hooks/use-configured-car-static-catalog';
import { DriveTrain } from '~/graphql/mapper/map-carline';
import { CatalogItemType } from '~/graphql/types';
import { state } from '~/storage';
import { technicalDataPresent } from '~/app/utils/technical-data-present';

type CompareLayerWrapperProps = {
	data?: Partial<CompareLayerProps['data']>;
	setIsOpen: (isOpen: boolean) => void;
	selectedDriveTrain?: DriveTrain;
	open: boolean;
	hasToAddTrimline?: boolean;
} & Pick<CompareLayerProps, 'actions' | 'config'>;

export function CompareLayerWrapper({
	data,
	actions,
	config,
	setIsOpen,
	selectedDriveTrain: selectedDriveTrainProp,
	open,
	hasToAddTrimline,
}: CompareLayerWrapperProps) {
	const marketConfig = useMarketConfig();
	const { featureServices } = useAppServices();
	const { isDebug } = useDebugMode();
	const { carIdentifier } = useConfigurator();
	const carlineData = useCarlineQuery(carIdentifier, marketConfig.config.financingInput);
	const disclaimerText = useAtomValue(state.financingDisclaimerText);
	const [defaultIdentifer] = useAtom(state.defaultIdentifier);

	const [activeTab, setActiveTab] = useState<string>(config?.selectedTab ?? 'technical-data');
	const { trimlines } = useCatalog();
	const { selectCard } = useActiveCard();

	const driveTrains = trimlines?.flatMap((t) => t.driveTrains) ?? [];
	const driveTrain = driveTrains?.find((d) => d.status.selected);

	const [selectedDriveTrain, setSelectedDriveTrain] = useState<DriveTrain | null>(
		selectedDriveTrainProp ?? driveTrain ?? null,
	);
	const [selectedCompareDriveTrain, setSelectedCompareDriveTrain] = useState<DriveTrain | null>(null);

	const configuredCar = carlineData.carline?.configuredCar;
	const customLayerId = 'custom-footnotes-layer';

	const {
		fetchStandardEquipment: fetchFirstEngineStandardEquipment,
		selectedStandardEquipment: firstEngineStandardEquipmentData,
	} = useConfiguredCarStaticCatalog();

	const {
		fetchDynamicCatalog: fetchFirstEngineDynamicCatalog,
		selectedStandardEquipmentDynamic: firstEngineStandardEquipmentDynamicData,
	} = useConfiguredCarDynamicCatalog();

	const {
		fetchStandardEquipment: fetchCompareEngineStandardEquipment,
		selectedStandardEquipment: compareEngineStandardEquipmentData,
	} = useConfiguredCarStaticCatalog();

	const {
		fetchDynamicCatalog: fetchCompareEngineDynamicCatalog,
		selectedStandardEquipmentDynamic: comapreEngineStandardEquipmentDynamicData,
	} = useConfiguredCarDynamicCatalog();

	const handleOnChange = (modelId: DriveTrainModelId) => {
		const currentDriveTrain = getCorrectDriveTrain(driveTrains, modelId);
		setSelectedDriveTrain(currentDriveTrain as DriveTrain);
	};

	const handleOnChangeCompare = (modelId: DriveTrainModelId) => {
		const currentDriveTrain = getCorrectDriveTrain(driveTrains, modelId);
		setSelectedCompareDriveTrain(currentDriveTrain as DriveTrain);
	};

	const handleAddDriveTrain = (engine: CompareLayerProps['data']['selectedDriveTrain']) => {
		const currentDriveTrain = getCorrectDriveTrain(driveTrains, engine?.modelId as DriveTrainModelId);
		actions?.onAddToConfiguration?.(engine!);
		selectCard(-1, currentDriveTrain as CatalogItemType['driveTrain']);
		setIsOpen(false);
	};

	const handleAddTrimline = (engine: CompareLayerProps['data']['selectedDriveTrain']) => {
		const currentTrimline = getCorrectTrimline(trimlines ?? [], engine);
		actions?.onAddToConfiguration?.(engine!);
		selectCard(-1, currentTrimline as CatalogItemType['trimline']);
		setIsOpen(false);
	};

	useEffect(() => {
		if (activeTab !== 'standardEquipment') return;

		// When compare/engine selection is disabled, we use the CarIdentifier of the selcted car to remove standard equipment that got substituted by optional Equipment
		const carIdentifierSelected = config?.isDisabled
			? carIdentifier
			: defaultIdentifer?.find(
					(a) =>
						a?.model?.code === selectedDriveTrain?.modelId?.code &&
						a?.model?.extensions?.toString() ===
							selectedDriveTrain?.__item?.modelId?.extensionsPR7?.toString(),
				);
		if (selectedDriveTrain && carIdentifierSelected) {
			const carlineInput = createCarlineInput(marketConfig.config.carIdentifier, selectedDriveTrain);
			fetchFirstEngineStandardEquipment(carlineInput);
			fetchFirstEngineDynamicCatalog(carIdentifierSelected);
		}
	}, [activeTab, marketConfig.config.carIdentifier, selectedDriveTrain, fetchFirstEngineStandardEquipment]);

	useEffect(() => {
		if (activeTab !== 'standardEquipment') return;
		const carIdentifierCompare = defaultIdentifer?.find(
			(a) =>
				a?.model?.code === selectedCompareDriveTrain?.modelId?.code &&
				a?.model?.extensions?.toString() ===
					selectedCompareDriveTrain?.__item?.modelId?.extensionsPR7?.toString(),
		);
		if (selectedCompareDriveTrain && carIdentifierCompare) {
			const carlineInput = createCarlineInput(marketConfig.config.carIdentifier, selectedCompareDriveTrain);
			fetchCompareEngineStandardEquipment(carlineInput);
			fetchCompareEngineDynamicCatalog(carIdentifierCompare);
		}
	}, [activeTab, marketConfig.config.carIdentifier, selectedCompareDriveTrain, fetchCompareEngineStandardEquipment]);

	if (!selectedDriveTrain) return null;

	return (
		<div className={customLayerId} data-fefa-layer-active={open}>
			<CompareLayer
				data={{
					footnoteLayerId: customLayerId,
					financialDisclaimer: disclaimerText,
					selectedDriveTrain: {
						...selectedDriveTrain.__item,
						trimlineId: selectedDriveTrain?.trimlineId,
					},
					standardEquipmentInitialCar:
						firstEngineStandardEquipmentData?.configuredCarStaticCatalog?.filter((a) =>
							firstEngineStandardEquipmentDynamicData?.configuredCar?.catalog?.features?.data?.some(
								(b) => b?.pr3 === a?.pr3,
							),
						) ?? [],
					standardEquipmentCompareCar:
						compareEngineStandardEquipmentData?.configuredCarStaticCatalog?.filter((a) =>
							comapreEngineStandardEquipmentDynamicData?.configuredCar?.catalog?.features?.data?.some(
								(b) => b?.pr3 === a?.pr3,
							),
						) ?? [],
					configuredCar,
					...data,
				}}
				language={marketConfig.language}
				actions={{
					onChangeActiveTab: (tab) => setActiveTab(tab),
					onAddToConfiguration: hasToAddTrimline ? handleAddTrimline : handleAddDriveTrain,
					onChangeInitialStandardEquipmentCar: (car) => handleOnChange(car?.modelId as DriveTrainModelId),
					onChangeCompareStandardEquipmentCar: (car) =>
						handleOnChangeCompare(car?.modelId as DriveTrainModelId),
					onChangeInitialTechDataCar: (car) => handleOnChange(car?.modelId as DriveTrainModelId),
					onChangeCompareTechDataCar: (car) => handleOnChangeCompare(car?.modelId as DriveTrainModelId),
				}}
				marketConfig={
					{
						...marketConfig.config,
						showRates: !marketConfig.config.hideMonthlyRates,
					} as unknown as CompareLayerProps['marketConfig']
				}
				services={{
					i18nService: featureServices.i18nService,
					numberFormatterService: featureServices.numberFormatterService,
					footnoteReferenceService: featureServices.footnoteReferenceService,
					footnoteService: featureServices.footnoteService,
				}}
				config={{
					...config,
					hideTabs:
						!marketConfig.config.hasTechnicalDataEnabled ||
						!technicalDataPresent(selectedDriveTrain.techData),
					isDebugMode: isDebug,
					selectedTab: activeTab as ActiveTab,
					isSelectedDriveTrain: selectedDriveTrain?.status.selected,
					isSelectedDriveTrainCompare: selectedCompareDriveTrain?.status.selected,
				}}
			/>
		</div>
	);
}
